import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useExperiences, experienceEmoji, type ExperienceCategory } from '@/hooks/useExperiences';
import { useConcertBackdrop } from '@/hooks/useConcertBackdrop';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { PageBanner } from '@/components/ui/PageBanner';
import { formatCOP } from '@/lib/format';
import { categoryTheme } from '@/lib/categoryTheme';

const CATEGORY_LABELS: Record<ExperienceCategory | 'all', string> = {
  all: 'Todas',
  cena: 'Cenas',
  viaje: 'Viajes',
  encuentro: 'Encuentros',
};

const CATEGORY_ORDER: ReadonlyArray<ExperienceCategory | 'all'> = [
  'all',
  'cena',
  'viaje',
  'encuentro',
];

/**
 * Experiences — ROUND7 P3 / ROUND8. Grid 2-col VIP responsive con filtro por
 * categoría. Datos reales desde `public.experiences` vía `useExperiences()`.
 */
export function ExperiencesPage(): JSX.Element {
  const [filter, setFilter] = useState<ExperienceCategory | 'all'>('all');
  const { data: experiences, isLoading, isError } = useExperiences();
  const heroImage = useConcertBackdrop();

  const all = experiences ?? [];
  const visible = filter === 'all' ? all : all.filter((e) => e.category === filter);

  return (
    <>
      <PageBanner
        eyebrow="Experiencias"
        title="Experiencias VIP"
        subtitle="Cenas privadas, viajes curados y encuentros de alto nivel — diseñados para que cada ocasión cuente. Reservá con tu cuenta y el resto lo hace nuestro concierge."
        heroImage={heroImage}
      />
      <section className="container-fc py-12 md:py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" aria-busy="true">
            {[0, 1].map((i) => (
              <Card key={i} variant="outlined" padded>
                <div className="h-6 w-2/3 rounded bg-neutral-100" />
                <div className="mt-3 h-4 w-full rounded bg-neutral-100" />
                <div className="mt-5 h-9 w-32 rounded bg-neutral-100" />
              </Card>
            ))}
          </div>
        ) : isError ? (
          <Card variant="outlined" padded>
            <p className="text-fc-muted" role="alert">
              No pudimos cargar las experiencias en este momento. Probá recargar la página en unos
              minutos.
            </p>
          </Card>
        ) : all.length === 0 ? (
          <Card variant="outlined" padded>
            <p className="text-fc-muted">
              No hay experiencias disponibles por ahora. Volvé más tarde.
            </p>
          </Card>
        ) : (
          <>
            <nav aria-label="Categorías" className="mb-6 flex flex-wrap gap-2">
              {CATEGORY_ORDER.map((cat) => (
                <CategoryChip key={cat} active={filter === cat} onClick={() => setFilter(cat)}>
                  {CATEGORY_LABELS[cat]}
                </CategoryChip>
              ))}
            </nav>

            {visible.length === 0 ? (
              <Card variant="outlined" padded>
                <p className="text-fc-muted">No hay experiencias en esta categoría por ahora.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {visible.map((exp) => {
                  const theme = categoryTheme(exp.category);
                  return (
                    <Card key={exp.id} variant="default" padded>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span aria-hidden className="text-3xl">
                            {experienceEmoji(exp.category)}
                          </span>
                          <div>
                            <Badge tone={theme.tone}>{theme.label}</Badge>
                            <CardTitle className="mt-2">{exp.title}</CardTitle>
                            {exp.subtitle && (
                              <p className="mt-0.5 text-xs text-fc-muted">{exp.subtitle}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {exp.description && (
                        <p className="mt-4 line-clamp-3 text-sm text-fc-muted">{exp.description}</p>
                      )}

                      <dl className="mt-4 grid grid-cols-2 gap-y-1 text-xs">
                        {exp.location && (
                          <>
                            <dt className="text-fc-muted">Lugar</dt>
                            <dd className="text-fc-ink">{exp.location}</dd>
                          </>
                        )}
                        {exp.duration && (
                          <>
                            <dt className="text-fc-muted">Duración</dt>
                            <dd className="text-fc-ink">{exp.duration}</dd>
                          </>
                        )}
                        {exp.maxCapacity != null && (
                          <>
                            <dt className="text-fc-muted">Capacidad</dt>
                            <dd className="text-fc-ink">{exp.maxCapacity} personas</dd>
                          </>
                        )}
                      </dl>

                      <div className="mt-5 flex items-center justify-between gap-3">
                        <span className="text-base font-semibold tabular-nums">
                          {formatCOP(exp.priceCents)}
                        </span>
                        <Button as={Link} to={`/experiences/${exp.id}`} variant="primary" size="sm">
                          Ver detalle
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-card px-3 py-1.5 text-sm transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        active
          ? 'bg-primary text-fc-bg'
          : 'border border-neutral-200 bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
